import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isTokenExpired,
  refreshAccessToken,
  parseJwtPayload,
} from './auth';
import { trpc, queryClient } from './trpc';

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  plan: string;
  verified: boolean;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  googleSignIn: (idToken: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<boolean>;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // tRPC mutations
  const loginMutation = trpc.auth.login.useMutation();
  const registerMutation = trpc.auth.register.useMutation();
  const googleSignInMutation = trpc.auth.googleSignIn.useMutation();

  // Query admin status when user is logged in
  const { data: adminData } = trpc.admin.isAdmin.useQuery(undefined, {
    enabled: !!user,
    retry: false,
  });
  const isAdmin = adminData?.isAdmin ?? false;

  /**
   * Extract user info from JWT token
   */
  const getUserFromToken = useCallback((): User | null => {
    const token = getAccessToken();
    if (!token) return null;

    const payload = parseJwtPayload(token);
    if (!payload) return null;

    return {
      id: typeof payload.userId === 'string' ? payload.userId : '',
      email: typeof payload.email === 'string' ? payload.email : '',
      plan: typeof payload.plan === 'string' ? payload.plan : 'free',
      verified: typeof payload.verified === 'boolean' ? payload.verified : false,
    };
  }, []);

  /**
   * Refresh authentication state
   * Returns true if user is authenticated, false otherwise
   */
  const refreshAuth = useCallback(async (): Promise<boolean> => {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    // No tokens - not authenticated
    if (!accessToken && !refreshToken) {
      setUser(null);
      return false;
    }

    // Access token is valid
    if (accessToken && !isTokenExpired(accessToken)) {
      const userInfo = getUserFromToken();
      setUser(userInfo);
      return true;
    }

    // Try to refresh the access token
    if (refreshToken && !isTokenExpired(refreshToken, 0)) {
      try {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          const userInfo = getUserFromToken();
          setUser(userInfo);
          return true;
        }
      } catch {
        // Refresh failed
      }
    }

    // Clear tokens and user
    clearTokens();
    setUser(null);
    return false;
  }, [getUserFromToken]);

  /**
   * Login user with email and password
   */
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      // Clear any stale cached data from previous user sessions
      queryClient.clear();
      const result = await loginMutation.mutateAsync({ email, password });
      setTokens(result.tokens.accessToken, result.tokens.refreshToken);
      setUser({
        id: result.user.id,
        email: result.user.email,
        plan: result.user.plan,
        verified: result.user.verified,
      });
    },
    [loginMutation]
  );

  /**
   * Register new user with email and password
   */
  const register = useCallback(
    async (email: string, password: string): Promise<void> => {
      // Clear any stale cached data from previous user sessions
      queryClient.clear();
      const result = await registerMutation.mutateAsync({ email, password });
      setTokens(result.tokens.accessToken, result.tokens.refreshToken);
      setUser({
        id: result.user.id,
        email: result.user.email,
        plan: result.user.plan,
        verified: result.user.verified,
      });
    },
    [registerMutation]
  );

  /**
   * Sign in with Google OAuth
   */
  const googleSignIn = useCallback(
    async (idToken: string): Promise<void> => {
      // Clear any stale cached data from previous user sessions
      queryClient.clear();
      const result = await googleSignInMutation.mutateAsync({ idToken });
      setTokens(result.tokens.accessToken, result.tokens.refreshToken);
      setUser({
        id: result.user.id,
        email: result.user.email,
        plan: result.user.plan,
        verified: result.user.verified,
      });
    },
    [googleSignInMutation]
  );

  /**
   * Logout user
   * Clears tokens, React Query cache, and redirects to login
   */
  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
    // Clear all cached queries to prevent data from showing for the wrong user
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [navigate]);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      await refreshAuth();
      setIsLoading(false);
    };

    initAuth();
  }, [refreshAuth]);

  // Set up automatic token refresh
  useEffect(() => {
    // Check token validity every minute
    const intervalId = setInterval(async () => {
      const accessToken = getAccessToken();
      if (accessToken && isTokenExpired(accessToken, 120)) {
        // Token expires in less than 2 minutes, try to refresh
        await refreshAuth();
      }
    }, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, [refreshAuth]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      isAdmin,
      login,
      register,
      googleSignIn,
      logout,
      refreshAuth,
    }),
    [user, isLoading, isAdmin, login, register, googleSignIn, logout, refreshAuth]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access auth context
 * Must be used within an AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ============================================================================
// Exports
// ============================================================================

export { AuthContext };
export default AuthProvider;
